import { FC, useMemo } from "react";
import { Skeleton, Text } from "@chakra-ui/react";
import { useFlagsStatus, useVariant } from "@unleash/proxy-client-react";

import {
  isStringWithNumber,
  getMainPaymentMethod,
  formatPriceWithoutZeros,
} from "../../../../../utils";
import { UnleashFlags } from "../../../../../constants";
import { useGetCardsQuery } from "../../../../../api/cards";
import { PaymentBlockCommissionError } from "./PaymentBlockCommissionError";

interface Props {
  amountFromInput: string | undefined;
  totalDebtAmount: number;
  isTotalDebtAmountExceeded: boolean;
}

export const PaymentBlockMessage: FC<Props> = ({
  amountFromInput,
  totalDebtAmount,
  isTotalDebtAmountExceeded,
}) => {
  const flagsStatus = useFlagsStatus();
  const flagsReady = flagsStatus.flagsReady;
  const flagsError = Boolean(flagsStatus.flagsError);

  const { data: cards } = useGetCardsQuery();

  const mainPaymentMethod = useMemo(() => {
    return getMainPaymentMethod(cards);
  }, [cards]);

  const formattedAmountFromInput = useMemo(() => {
    const result = amountFromInput?.replace(/[^\d,.]/g, "").replace(",", ".");
    if (!isStringWithNumber(result)) {
      return "0";
    }

    return result;
  }, [amountFromInput]);

  const commissionVariant = useVariant(UnleashFlags.dfPaymentsCommission);
  const commissionValue = commissionVariant.payload?.value;
  const commissionAmount
      = mainPaymentMethod?.terminalType === "PAID"
      && isStringWithNumber(commissionValue)
      && isStringWithNumber(formattedAmountFromInput)
        ? Number(formattedAmountFromInput) * (Number(commissionValue) / 100)
        : 0;

  const message = useMemo(() => {
    let result = "";

    if (mainPaymentMethod?.terminalType === "PAID" && flagsReady) {
      const amountPlusCommission = Number(formattedAmountFromInput) + commissionAmount;
      const amountPlusCommissionWithCurrency = formatPriceWithoutZeros(amountPlusCommission);

      result = `с учётом комиссии спишем ${amountPlusCommissionWithCurrency}`;
    }

    if (isTotalDebtAmountExceeded) {
      const totalDebtAmountWithCurrency = formatPriceWithoutZeros(totalDebtAmount);
      result = `Максимальная сумма платежа ${totalDebtAmountWithCurrency}`;
    }

    return result;
  }, [
    flagsReady,
    mainPaymentMethod,
    formattedAmountFromInput,
    commissionAmount,
    isTotalDebtAmountExceeded,
    totalDebtAmount,
  ]);

  if (mainPaymentMethod?.terminalType === "PAID" && !flagsReady && !flagsError) {
    return <Skeleton h="18px" mt="4px" />;
  }

  if (mainPaymentMethod?.terminalType === "PAID" && (flagsError || (flagsReady && !isStringWithNumber(commissionValue)))) {
    return <PaymentBlockCommissionError />;
  }

  return (
    <>
      {message && (
        <Text variant={isTotalDebtAmountExceeded ? "red" : "gray"} fontSize="12px" mt="2px">
          {message}
        </Text>
      )}
    </>
  );
};
